import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, UserPlus, BookOpen, Heart, TrendingUp, Users, Target, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { statistics, howWeDoIt, partners } from '../data/mock';

const iconMap = {
  UserPlus: UserPlus,
  BookOpen: BookOpen,
  Heart: Heart,
  TrendingUp: TrendingUp,
};

// Color mapping for each pillar matching the logo colors
const pillarColors = {
  recruitment: { bg: 'from-brand-gold to-gold-600', shadow: 'shadow-gold-500/20' },
  development: { bg: 'from-brand-green to-green-600', shadow: 'shadow-green-500/20' },
  retention: { bg: 'from-brand-red to-red-600', shadow: 'shadow-red-500/20' },
  advancement: { bg: 'from-brand-navy to-navy-700', shadow: 'shadow-navy-500/20' },
};

export default function HomePage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-navy-50 via-white to-gold-50/30">
        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-96 h-96 bg-gold-100/40 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-0 w-96 h-96 bg-green-100/40 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-50 border border-gold-200 mb-6">
                <Sparkles className="w-4 h-4 text-brand-gold" />
                <span className="text-sm font-medium text-gold-700">Empowering Educators Since 2022</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-900 leading-tight mb-6">
                You are the{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-green-600">
                  missing piece
                </span>
              </h1>
              
              <p className="text-lg text-navy-600 leading-relaxed mb-8 max-w-xl">
                Transforming early childhood education in D.C. by supporting men in ECE with a focus on equity, inclusion, comradeship and professional development. Join us in advancing educational outcomes for children of color.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/membership">
                  <Button size="lg" className="bg-brand-green hover:bg-green-600 text-white font-semibold px-8 shadow-xl shadow-green-500/20 hover:shadow-green-500/30 transition-all duration-300 w-full sm:w-auto">
                    Become a Member
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button size="lg" variant="outline" className="border-2 border-navy-200 text-navy-700 font-semibold px-8 hover:bg-navy-50 transition-all duration-300 w-full sm:w-auto">
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Hero Image - Conference Group Photo */}
            <div className="relative hidden lg:block">
              <div className="relative w-full max-w-lg mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-gold-100 to-green-50 rounded-3xl transform rotate-3 blur-sm" />
                <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
                  <img 
                    src="/images/conference-group.jpg" 
                    alt="DC Men in ECE Conference Group"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-brand-gold font-medium mb-2">The Reality We're Changing</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white">
              It's time to change the statistic
            </h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {statistics.map((stat, index) => {
              const colors = ['text-brand-gold', 'text-brand-red', 'text-brand-green', 'text-white'];
              return (
                <div key={index} className="text-center p-6 rounded-2xl bg-navy-800/50 border border-navy-700/50 hover:border-brand-gold/30 transition-colors duration-300">
                  <div className={`text-3xl sm:text-4xl font-bold ${colors[index]} mb-2`}>{stat.value}</div>
                  <p className="text-navy-300 text-sm">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Do It Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-50 border border-gold-200 mb-6">
              <Target className="w-4 h-4 text-brand-gold" />
              <span className="text-sm font-medium text-gold-700">Our Approach</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">How We Do It</h2>
            <p className="text-navy-600">Our comprehensive approach focuses on four key pillars to support men of color throughout their ECE journey.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {howWeDoIt.map((item, index) => {
              const Icon = iconMap[item.icon];
              const colors = pillarColors[item.id];
              return (
                <Card key={item.id} className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500 overflow-hidden">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-5">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colors.bg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg ${colors.shadow}`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-navy-900 mb-3 group-hover:text-brand-green transition-colors duration-300">{item.title}</h3>
                        <p className="text-navy-600 leading-relaxed mb-4">{item.description}</p>
                        <Link to={item.link} className="inline-flex items-center text-brand-green font-semibold hover:text-green-700 transition-colors duration-200">
                          Learn More
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 bg-gradient-to-br from-brand-green to-green-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">About Us</h2>
              <p className="text-green-100 text-lg leading-relaxed mb-6">
                DC Men in Early Childhood Education (ECE) is an initiative of the DC Early Learning Collaborative, a public policy organization that championed the passage of DC's universal Pre-K legislation.
              </p>
              <p className="text-green-100 leading-relaxed mb-8">
                Our mission focuses on the recruitment, development, retention, and advancement of men of color in ECE through supportive communities and professional development. Founded in April 2022, we promote equity and inclusion, aiming to improve educational outcomes for children of color.
              </p>
              <Link to="/about">
                <Button size="lg" className="bg-white text-brand-green font-semibold hover:bg-green-50 shadow-xl transition-all duration-300">
                  Learn Our Story
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="text-4xl font-bold text-white mb-1">2022</div>
                  <p className="text-green-200 text-sm">Year Founded</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="text-4xl font-bold text-brand-gold mb-1">40+</div>
                  <p className="text-green-200 text-sm">First Attendees</p>
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="text-4xl font-bold text-white mb-1">200+</div>
                  <p className="text-green-200 text-sm">2023 Conference</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="text-4xl font-bold text-brand-gold mb-1">D.C.</div>
                  <p className="text-green-200 text-sm">Based & Growing</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-navy-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-navy-500 font-medium mb-2">Trusted By</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-navy-900">Just a Few of Our Partners</h2>
          </div>
          
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-12">
            {partners.map((partner, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-navy-100 hover:shadow-md hover:border-brand-gold/30 transition-all duration-300 flex items-center justify-center">
                <img 
                  src={partner.logo} 
                  alt={partner.name}
                  className="h-16 md:h-20 w-auto object-contain max-w-[160px]"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">
            Ready to Make a Difference?
          </h2>
          <p className="text-lg text-navy-600 mb-10 max-w-2xl mx-auto">
            Join us in transforming the early childhood education landscape in Washington, D.C. and beyond. Your journey starts here.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/membership">
              <Button size="lg" className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold px-8 shadow-xl shadow-gold-500/25 hover:shadow-gold-500/40 transition-all duration-300">
                Join DC Men in ECE
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/conference">
              <Button size="lg" variant="outline" className="border-2 border-navy-200 text-navy-700 font-semibold px-8 hover:bg-navy-50">
                Attend Our Conference
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
